import React from "react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import type { TokenVault, VaultUsage, VaultType } from "@/types/billing";

/**
 * VaultUsageMeter
 * Displays a usage meter for a token vault or vault usage summary.
 * Color-coded: green (<60%), yellow (60-80%), red (>80%).
 * Shows percentage only (not dollar amounts per spec).
 */

interface VaultUsageMeterProps {
  vault: TokenVault | VaultUsage;
}

const VAULT_TYPE_LABELS: Record<VaultType, string> = {
  team: "Team",
  project: "Project",
  personal: "Personal",
  general: "General",
};

function getUsageColor(percent: number): string {
  if (percent >= 80) return "text-red-600";
  if (percent >= 60) return "text-yellow-600";
  return "text-green-600";
}

function getProgressColor(percent: number): string {
  if (percent >= 80) return "[&>div]:bg-red-500";
  if (percent >= 60) return "[&>div]:bg-yellow-500";
  return "[&>div]:bg-green-500";
}

function getVaultName(vault: TokenVault | VaultUsage): string {
  if ("name" in vault) return vault.name;
  if ("vaultName" in vault) return vault.vaultName;
  return "Vault";
}

function getVaultType(vault: TokenVault | VaultUsage): VaultType {
  return vault.type;
}

function getUsagePercent(vault: TokenVault | VaultUsage): number {
  return vault.usagePercent;
}

function getResetDate(vault: TokenVault | VaultUsage): string | undefined {
  if ("resetDate" in vault) return vault.resetDate;
  if ("currentPeriodEnd" in vault) return vault.currentPeriodEnd;
  return undefined;
}

function formatResetDate(dateStr: string | undefined): string {
  if (!dateStr) return "";
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

export const VaultUsageMeter: React.FC<VaultUsageMeterProps> = ({ vault }) => {
  const name = getVaultName(vault);
  const type = getVaultType(vault);
  const percent = Math.min(100, Math.max(0, getUsagePercent(vault)));
  const resetDate = getResetDate(vault);
  const resetLabel = formatResetDate(resetDate);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground">{name}</span>
          <Badge variant="outline" className="text-xs capitalize">
            {VAULT_TYPE_LABELS[type]}
          </Badge>
        </div>
        <span className={`text-sm font-semibold ${getUsageColor(percent)}`}>
          {Math.round(percent)}%
        </span>
      </div>
      <Progress
        value={percent}
        className={`h-2 ${getProgressColor(percent)}`}
      />
      {resetLabel && (
        <p className="text-xs text-muted-foreground">
          Resets {resetLabel}
        </p>
      )}
    </div>
  );
};
